// Growem - Resumen (home). Today at a glance: greeting, streak week-map, session CTA,
// today's bitácora tiles, and a strength trend chart. Reads/writes only via useStore/useUI.

import { useEffect, useMemo, useRef, useState } from 'react'
import { useStore } from '../state/store'
import { buildStrengthModel, trendOf, rebased, TREND_GATE, type IndexPoint } from '../lib/strength'
import { useUI } from '../state/ui'
import { planForDate, GOALS } from '../data/plan'
import {
  basketEvents, clusterEvents, colorFor, clip, eventRows, leadFor, markerKind, noteFor, segmentsOf,
  CARRIED_DASH, DIM_COLOR, DIM_OPACITY, LOWCONF_DASH, type PointMeta, type Segment,
} from '../lib/chartMarks'
import { dateLabel, dow, addDays, localHour, parseDay } from '../domain/dates'
// El indice de plan del dia sale de UNA sola formula compartida (planIdxOf), nunca del dia de la
// semana: si la rutina se movio, esta pantalla tiene que contar la que de verdad se hace hoy.
import { planIdxOf, isMovedSession, planDayWeekday } from '../domain/planDay'
import { sessionProgress, dayCounts, proteinDebt } from '../domain/rules'
import { todayTasks, selectTasksView, weeklyWeight, type Task, type WeeklyWeight } from '../domain/tasks'

const fmt = (n: number) => n.toLocaleString('en-US')

export default function Resumen() {
  const { getDay, today, profile, streakNow, setTrainAt, toggleCreatine, setSleep, setWeight, state } = useStore()
  const { setTab } = useUI()
  const d = getDay(today)
  const [sel, setSel] = useState<string>('all')

  const hr = localHour()
  // Rutina EFECTIVA de hoy. "Descanso" ya no es "es domingo", sino "no hay rutina asignada": asi un
  // domingo con una rutina recuperada se pinta como sesion viva sin tocar nada mas.
  const pIdx = planIdxOf(d)
  const isRest = pIdx == null
  const dayName = pIdx != null ? planForDate(today)[pIdx]?.name ?? 'Descanso' : 'Descanso'
  // Si la rutina viene movida de otro dia, se dice de que dia es (el nombre nunca va escrito a mano).
  const movedFrom = isMovedSession(d) ? planDayWeekday(pIdx) : null
  const { doneN, totN } = sessionProgress(d, pIdx)
  const pct = totN > 0 ? Math.round((doneN / totN) * 100) : 0

  // ---- streak week-map: from earliest recorded day → today, capped at 30 ----
  const keys = Object.keys(state.days).sort()
  const firstRec = keys[0] ?? today
  const mapDays: string[] = []
  for (let i = 29; i >= 0; i--) {
    const iso = addDays(today, -i)
    if (iso < firstRec) continue // ISO strings compare lexicographically
    mapDays.push(iso)
  }
  // El mapa se queda por DIA DE CALENDARIO a proposito: pinta "hubo series" (dayCounts) y el domingo
  // de descanso. Si mueves la rutina del martes al miercoles, el martes sigue en gris: el dia origen
  // no se sana. Es la misma regla que ya gobierna la racha y cambiarla es decision de producto aparte.
  const cellColor = (iso: string) =>
    dayCounts(state.days[iso]) ? 'var(--volt)' : dow(iso) === 0 ? 'var(--restday)' : 'var(--line-10)'

  // ---- protein debt (after 6pm) ----
  const debt = proteinDebt(d.prot, hr)
  const protAlert = hr >= GOALS.proteinDebtHour && debt > 0

  return (
    <>
      {/* 1 · greeting */}
      <div className="card">
        <div className="h1">Arriba, {profile.displayName}.</div>
        <div className="kick" style={{ marginTop: 6 }}>{dateLabel(today)}</div>
      </div>

      {/* 2 · tareas de hoy (LAYER 1) */}
      <TareasHoy />

      {/* 3 · streak */}
      <div className="card">
        <div className="kick">racha</div>
        <div className="row" style={{ alignItems: 'baseline', gap: 8, marginTop: 4 }}>
          <span className="disp" style={{ fontSize: 28, fontWeight: 700, color: 'var(--volt)' }}>{streakNow()}</span>
          <span className="muted" style={{ fontSize: 12.5 }}>días · mejor {profile.bestStreak}</span>
        </div>
        <div style={{ display: 'flex', gap: 3, marginTop: 12 }}>
          {mapDays.map((iso) => (
            <button
              key={iso}
              onClick={() => setTab('sesion')}
              title={iso}
              style={{
                flex: 1, height: 18, borderRadius: 3, padding: 0,
                background: cellColor(iso),
                border: iso === today ? '1px solid var(--volt-border-50)' : '1px solid transparent',
              }}
            />
          ))}
        </div>
      </div>

      {/* 4 · sesión de hoy */}
      <button className="card" onClick={() => setTab('sesion')} style={{ display: 'block', width: '100%', textAlign: 'left' }}>
        <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
          <div className="kick">sesión de hoy</div>
          {totN > 0 && <span className="muted" style={{ fontSize: 12 }}>{doneN}/{totN}</span>}
        </div>
        <div className="disp" style={{ fontSize: 18, fontWeight: 700, marginTop: 4 }}>{dayName}</div>
        {/* El dia de origen sale de planDayWeekday, nunca escrito a mano: si el plan cambia, el texto
            cambia con el. */}
        {movedFrom && <div className="note" style={{ marginTop: 4 }}>Es la rutina del {movedFrom}, movida a hoy.</div>}
        {totN > 0 && (
          <div className="bar" style={{ marginTop: 10 }}><i style={{ width: `${pct}%` }} /></div>
        )}
        <div style={{ marginTop: 10 }}>
          {isRest ? (
            // Ya no dice "Domingo" porque la condicion dejo de ser el dia de la semana: es "hoy no
            // hay rutina". Un domingo con rutina recuperada entra por la rama normal.
            <span className="tip">Hoy descansas: recupera para volver más fuerte.</span>
          ) : d.trainAt ? (
            <span className="note">Hoy entrenas {d.trainAt}</span>
          ) : (
            <span
              role="button"
              tabIndex={0}
              className="link"
              style={{ fontSize: 13.5 }}
              onClick={(e) => {
                e.stopPropagation()
                const v = window.prompt('¿A qué hora entrenas hoy? (HH:MM)', '18:00')
                if (v && v.trim()) setTrainAt(today, v.trim())
              }}
            >
              ¿A qué hora entrenas hoy? →
            </span>
          )}
        </div>
      </button>

      {/* 5 · bitácora de hoy */}
      <div className="card">
        <div className="kick" style={{ marginBottom: 10 }}>bitácora de hoy</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
          <Tile
            label="proteína" color="var(--volt)"
            value={`${d.prot}`} unit="g"
            sub={protAlert ? `−${debt} · batido ya` : `${GOALS.proteinMin}-${GOALS.proteinMax} g`}
            border={protAlert ? 'var(--warn-border)' : undefined}
            valueColor={protAlert ? 'var(--warn)' : undefined}
          />
          <Tile label="kcal" color="var(--warn)" value={fmt(d.kcal)} sub={`${fmt(GOALS.kcalMin)}-${fmt(GOALS.kcalMax)}`} />
          <Tile
            label="agua" color="var(--water)"
            value={(d.water / 1000).toFixed(1)} unit="L"
            sub={`${(GOALS.waterMinMl / 1000).toFixed(1)}-${(GOALS.waterMaxMl / 1000).toFixed(1)}`}
          />
          <Tile
            label="creatina" color="var(--green-2)"
            value={d.creatine ? '✓' : '-'} sub={`${GOALS.creatineG} g`}
            valueColor={d.creatine ? 'var(--volt)' : undefined}
            onClick={() => toggleCreatine(today)}
          />
          <Tile
            label="sueño" color="var(--sleep)"
            value={d.sleep || '-'} sub={`${GOALS.sleepMinH}-${GOALS.sleepMaxH} h`}
            onClick={() => {
              const v = window.prompt('Tu tramo de sueño más largo (ej. 3h30)', d.sleep ?? '')
              if (v && v.trim()) setSleep(today, v.trim())
            }}
          />
          <Tile
            label="peso" color="var(--ink-2)"
            value={d.weight != null ? `${d.weight}` : '-'} unit={d.weight != null ? 'kg' : undefined}
            sub="en ayunas · mañana"
            onClick={() => {
              // FIX #1 morning gating: the recomp protocol needs a fasted MORNING weight; a late
              // weigh-in is water noise. bodyweight_kg has no capture-hour, so we guide prospectively
              // (label + a late-hour warning) and still save whatever is entered - no data is destroyed.
              // FUTURE iOS NATIVE: fire an on-wake weigh-in reminder (UNUserNotificationCenter, ~07:00
              // local); if a capture-hour column is ever added, stamp localHour() here to flag late entries.
              const late = hr >= GOALS.morningHour
              const v = window.prompt(
                late
                  ? 'Peso en ayunas (kg). Lo ideal es pesarte al despertar en ayunas; un peso de la tarde es ruido, pero se guarda igual.'
                  : 'Peso en ayunas (kg)',
                d.weight != null ? String(d.weight) : '',
              )
              if (v) {
                const n = Number(v.replace(',', '.'))
                if (!Number.isNaN(n) && n > 0) setWeight(today, n)
              }
            }}
          />
        </div>
      </div>

      {/* 6 · fuerza */}
      <StrengthCard sel={sel} setSel={setSel} />
    </>
  )
}

// ---------- bitácora tile ----------
function Tile(props: {
  label: string; color: string; value: string; unit?: string; sub: string
  onClick?: () => void; border?: string; valueColor?: string
}) {
  const { label, color, value, unit, sub, onClick, border, valueColor } = props
  const inner = (
    <>
      <span className="kick" style={{ color, fontSize: 9.5, letterSpacing: '0.12em' }}>{label}</span>
      <span className="disp" style={{ fontSize: 19, fontWeight: 700, color: valueColor ?? 'var(--ink-1)', lineHeight: 1.1 }}>
        {value}{unit && <span style={{ fontSize: 10, color: 'var(--ink-4)', marginLeft: 2 }}>{unit}</span>}
      </span>
      <span style={{ fontSize: 9.5, color: 'var(--ink-4)' }}>{sub}</span>
    </>
  )
  const style: React.CSSProperties = {
    display: 'flex', flexDirection: 'column', gap: 3, textAlign: 'left',
    background: 'var(--surface-2)', border: `1px solid ${border ?? 'var(--line-06)'}`,
    borderRadius: 'var(--r-sm)', padding: '9px 10px',
  }
  return onClick
    ? <button type="button" onClick={onClick} style={style}>{inner}</button>
    : <div style={style}>{inner}</div>
}

// ---------- TAREAS DE HOY (LAYER 1) ----------
// Renders today's evidence-based habit cues as if-then implementation intentions, each a one-tap
// action wired to an EXISTING store/ui handler. Completion is read LIVE from the real metric that
// backs each habit (weight, sleep, session sets, protein, water, creatine) - there is no separate
// "task done" store. Only in-window + undone rows are shown, ranked by todayTasks' priority and
// capped at 3; when nothing is due it collapses to one calm "al día · n/total" line. The card also
// surfaces the honest 7-day rolling bodyweight average (FIX #2).
//
// FUTURE iOS NATIVE: these are cues the web can only render in-app. The native app should schedule
// the same in-window, not-done rows as UNUserNotificationCenter local notifications (time-sensitive
// level) at each row's window-open time, and badge the app icon with the undone count. The Task[]
// this card consumes already carries window + priority, so the native scheduler can reuse it as-is.
function TareasHoy() {
  const { getDay, today, state, setWeight, setSleep, toggleCreatine, addWater } = useStore()
  const { setTab, openMeal } = useUI()
  const d = getDay(today)
  const hr = localHour()
  // Misma fuente que la tarjeta de sesion (planIdxOf): si las dos derivaran distinto, la misma
  // pantalla se contradiria (la tarjeta diria Pull A y la tarea diria Legs A).
  const pIdx = planIdxOf(d)
  const meals = state.mealsByDay[today] ?? []
  const { visible, doneCount, total } = selectTasksView(todayTasks(d, meals, hr, pIdx))
  const ww = weeklyWeight(state.days)

  // Fire the one-tap action for a row via the existing handlers (no new mutation paths).
  const fire = (t: Task) => {
    switch (t.action) {
      case 'weigh': {
        // weigh is only in-window before GOALS.morningHour, so this prompt stays the fasted/morning one.
        const v = window.prompt('Peso en ayunas (kg)', d.weight != null ? String(d.weight) : '')
        if (v) { const n = Number(v.replace(',', '.')); if (!Number.isNaN(n) && n > 0) setWeight(today, n) }
        break
      }
      case 'sleep': {
        const v = window.prompt('Tu tramo de sueño más largo (ej. 3h30)', d.sleep ?? '')
        if (v && v.trim()) setSleep(today, v.trim())
        break
      }
      case 'train': setTab('sesion'); break
      case 'protein': openMeal(); break         // "comida real primero" - open the meal logger
      case 'water': addWater(today); break       // one cup (profile.cupSize)
      case 'creatine': if (!d.creatine) toggleCreatine(today); break // guard: toggle only false -> true
    }
  }

  const rowStyle = (pinned: boolean): React.CSSProperties => ({
    display: 'flex', flexDirection: 'column', gap: 2, width: '100%', textAlign: 'left',
    background: pinned ? 'var(--volt-fill-08)' : 'var(--surface-2)',
    border: `1px solid ${pinned ? 'var(--volt-border-30)' : 'var(--line-06)'}`,
    borderRadius: 'var(--r-sm)', padding: '10px 12px',
  })

  return (
    <div className="card">
      <div className="kick">tareas de hoy</div>
      {visible.length === 0 ? (
        <div className="note" style={{ display: 'block', marginTop: 8 }}>
          Al día · {doneCount}/{total} hábitos de hoy
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 10 }}>
          {visible.map((t, i) => (
            <button key={t.id} type="button" onClick={() => fire(t)} style={rowStyle(i === 0)}>
              <span className="disp" style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--ink-1)', lineHeight: 1.35 }}>{t.label}</span>
              {t.sub && <span style={{ fontSize: 11, color: 'var(--ink-4)', lineHeight: 1.4, marginTop: 2 }}>{t.sub}</span>}
            </button>
          ))}
        </div>
      )}
      {ww.avg7 != null && <WeeklyWeightLine ww={ww} />}
    </div>
  )
}

// FIX #2 · honest rolling bodyweight average. Two audit fixes are folded here:
//  - NEVER headline a 1-2 sample mean: the bold number is shown only once there are >= 3 fasted
//    weigh-ins (below that we show an encouraging line, never a figure that pretends to be a trend).
//    This closes the "one day's weight under a 7-day label" honesty gap from the evidence-UX audit.
//  - Label the window by what it ACTUALLY is (the last N weigh-ins, not calendar days): "promedio de
//    tus N pesajes" until 7 exist, then "promedio 7 pesajes", and "vs. los 7 pesajes previos". This
//    stops the copy overstating the window when weigh-ins are sparse.
// The direction WORD stays gated behind weeklyWeight's noise band (`enough`); it appears only when the
// average change clears the band, otherwise the line stays neutral and explains why.
function WeeklyWeightLine({ ww }: { ww: WeeklyWeight }) {
  const color = ww.trend === 'subiendo' ? 'var(--volt)' : ww.trend === 'bajando' ? 'var(--warn)' : 'var(--ink-3)'
  const showAvg = ww.avg7 != null && ww.count >= 3 // mustFix: no headline number under 3 weigh-ins
  const avgLabel = ww.windowN >= 7 ? 'promedio 7 pesajes' : `promedio de tus ${ww.windowN} pesajes`
  return (
    <div style={{ marginTop: 12, paddingTop: 10, borderTop: '1px solid var(--line-06)' }}>
      {showAvg ? (
        <>
          <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
            <span className="kick" style={{ fontSize: 9.5 }}>{avgLabel}</span>
            <span className="disp" style={{ fontSize: 15, fontWeight: 700 }}>
              {ww.avg7!.toFixed(1)}<span style={{ fontSize: 10, color: 'var(--ink-4)', marginLeft: 2 }}>kg</span>
            </span>
          </div>
          <div style={{ fontSize: 10.5, color: 'var(--ink-4)', marginTop: 4, lineHeight: 1.45 }}>
            {ww.enough && ww.deltaKg != null ? (
              <>
                <span style={{ color, fontWeight: 600 }}>
                  {ww.trend === 'estable' ? 'estable' : `${ww.trend} ${ww.deltaKg > 0 ? '+' : ''}${ww.deltaKg.toFixed(1)} kg`}
                </span>{' '}vs. los 7 pesajes previos. El peso diario varía 0.5-2 kg por agua; miramos el promedio, no un día suelto.
              </>
            ) : (
              <>El peso diario varía 0.5-2 kg por agua; con más pesajes en ayunas verás la tendencia del promedio (llevas {ww.count}).</>
            )}
          </div>
        </>
      ) : (
        <div style={{ fontSize: 10.5, color: 'var(--ink-4)', lineHeight: 1.45 }}>
          Pésate en ayunas al despertar: con 3 pesajes o más verás tu promedio (llevas {ww.count}).
        </div>
      )}
    </div>
  )
}

// ---------- strength chart ----------
// Real-calendar-time chart of the per-muscle-group CHAINED strength index (see lib/strength.ts).
// "Todas" → los indices de grupo como CONTEXTO gris mas el general encadenado en volt; tocar un
// grupo en la leyenda lo promueve a su color (foco + contexto: 10 lineas de colores a la vez eran
// ilegibles y ademas no son comparables entre si, cada indice tiene su propia base 100).
// Selecting a group → the group index (bold volt) over its per-exercise detail lines, all on one
// 100-baseline axis (Cleveland "emphasis" = the user's idea #3).
// Honesty gates: <3 sessions → dots only, no trend line, "datos insuficientes"; the direction word
// is emitted only when the net change clears the ±noise gate (trendOf), la cifra solo aparece si
// dobla ese umbral, y cada punto declara por que marca lo que marca (hueco = medicion menos
// fiable, anillo = paso recortado, tramo punteado = valor arrastrado, glifo = entro algo nuevo).
// preserveAspectRatio="meet" (not "none") so the slope isn't an artifact of screen width.

const PLOT = { x0: 8, x1: 300, y0: 16, y1: 150, w: 340, h: 172 } // plot box + label gutter (300→340)
// Canal libre de 16u sobre el area de trazado: ahi viven los glifos de cambio de canasta, asi que
// el viewBox NO cambia de tamaño (a 390px de ancho cualquier crecimiento se comeria la tarjeta).
const GLYPH_Y = 8
// Separacion minima entre glifos; por debajo comparten uno (clusterEvents). El valor va en UNIDADES
// DE VIEWBOX, no en pixeles (SF6): el svg mide 340u de ancho y ocupa el ancho util de la tarjeta,
// que a 390px de pantalla son 320px CSS (#view deja 354, la card se come 16+16 de padding y 2 de
// borde). O sea 1u = 320/340 = 0.94px en el caso mas estrecho y 1.04px si la tarjeta llega a 354.
// El objetivo tactil minimo es 24px CSS (WCAG 2.5.8), asi que hace falta 24/0.94 = 25.5u: con los
// 11u de antes dos glifos quedaban a ~10px de centro a centro siendo circulos de ~8.6px, imposibles
// de tocar por separado. 26u cubre el caso estrecho con margen; el unico efecto es que mas eventos
// comparten glifo, que ya esta resuelto (eventRows lista 3 y resume el resto).
const GLYPH_MIN_GAP = 26
const MAX_ROWS = 6       // tope duro de filas del tooltip: 6 filas = 76u de alto sobre 172 de viewBox

interface ChartLine {
  key: string
  name: string          // series display name (exercise · group label · 'general')
  pts: IndexPoint[]
  meta: PointMeta[]     // banderas de honestidad alineadas 1:1 con pts (hueco / anillo / arrastrado)
  segs: Segment[]       // tramos medidos vs arrastrados, ya partidos (SF30: fuera del render)
  raw?: number[]        // e1RM (kg) aligned 1:1 with pts - present ONLY on exercise detail lines.
                        // presence == show kg; absence == show 'índice N' (aggregates have no single kg)
  color: string         // color en REPOSO (las lineas de contexto ya nacen grises)
  width: number
  opacity: number
  bold?: boolean
  dim?: boolean         // linea de CONTEXTO (vista "Todas" sin resaltar): gris y sin marcadores
  hue?: string          // color propio: el que toma al resaltarla. Solo en las lineas de grupo
  groupKey?: string     // grupo al que responde el resaltado. Solo en las lineas de grupo
}

/** IndexPoint -> marcas. Un solo mapeador para que ninguna vista invente banderas propias. */
const metaOfIndex = (p: IndexPoint): PointMeta => ({ lowConf: p.lowConf, provisional: p.provisional, obs: p.obs })

// el punto tocado: cx/cy en unidades del viewBox; `rows` son las filas del tooltip y `notes` cuantas
// de las ULTIMAS filas son notas (se pintan atenuadas, igual que la fecha de la primera fila)
type ActivePt = { cx: number; cy: number; rows: string[]; notes: number }

function StrengthCard({ sel, setSel }: { sel: string; setSel: (s: string) => void }) {
  const { state } = useStore()
  const model = useMemo(() => buildStrengthModel(state.days), [state.days])
  const groups = model.groups

  // ---- interaction state (hooks MUST precede the groups.length===0 early return below) ----
  const svgRef = useRef<SVGSVGElement>(null)
  const downRef = useRef<{ x: number; y: number } | null>(null)   // tap-vs-scroll discriminator
  const [active, setActive] = useState<ActivePt | null>(null)
  const [focus, setFocus] = useState<string | null>(null)         // grupo resaltado en la vista "Todas"
  useEffect(() => { setActive(null); setFocus(null) }, [sel])     // clear a stale tooltip/resaltado on view change

  // Derivacion CARA arriba del early return y memoizada (MF18). `pick` llama a setActive en cada
  // pointermove del raton: sin este memo, los eventos de canasta (barrido de todos los grupos por
  // todos sus ejercicios) y la pendiente robusta de Theil-Sen se recalcularian en cada frame de
  // hover. Deps estables: `model` ya esta memoizado sobre [state.days] y `sel` no cambia al pasar
  // el puntero. `focus` NO entra aqui a proposito: solo decide colores, no datos.
  const view = useMemo(() => {
    const selGroup = sel === 'all' ? null : model.groups.find((g) => g.group === sel) ?? null
    // MF19: el modelo es compartido y memoizado; ordenar en sitio (selGroup.exercises.sort) lo
    // reordenaria para todos los consumidores y los colores bailarian entre renders. Copia SIEMPRE.
    const exOrdered = selGroup ? [...selGroup.exercises].sort((a, b) => a.name.localeCompare(b.name)) : []
    // Una sola derivacion de eventos alimenta los glifos Y el asterisco del badge (fuente unica).
    const events = basketEvents(selGroup ? [selGroup] : model.groups)
    const pts = selGroup ? selGroup.points : model.overview
    return { selGroup, exOrdered, events, tr: trendOf(pts, events.map((e) => e.date)) }
  }, [model, sel])
  const { selGroup, exOrdered, events, tr } = view

  // ---- geometria del grafico (SF30) ----
  // TODO lo caro vive en UN memo con dependencias estables: las banderas por punto, los tramos de
  // cada polilinea, la lista plana de puntos, los min/max de los dos ejes, los clusters de glifos y
  // la lista de impactos (que formatea una fecha POR PUNTO y por linea, con su Date y sus strings).
  // Antes todo eso se recalculaba en CADA render, y `pick` llama a setActive en cada pointermove
  // del raton: pasar el puntero por el grafico rehacia la geometria entera decenas de veces por
  // segundo, incluidas las 10 lineas de contexto que ni siquiera dibujan marcadores. `focus` NO
  // entra en las dependencias a proposito: solo decide el color y el grosor de las lineas de grupo,
  // y eso se aplica como ESTILO en el render (una copia superficial de la linea resaltada, nada mas).
  // Devuelve null cuando no hay ni un punto: la tarjeta sale por el estado vacio de abajo.
  const geom = useMemo(() => {
    const lines: ChartLine[] = []
    const add = (l: Omit<ChartLine, 'segs'>) =>
      // La polilinea deja de ser monolitica: segmentsOf separa los tramos MEDIDOS de los arrastrados
      // (nadie midio ese dia), que van punteados. Con menos de 2 puntos no hay tramo que dibujar.
      lines.push({ ...l, segs: l.pts.length >= 2 ? segmentsOf(l.meta) : [] })

    if (view.selGroup) {
      // per-exercise detail - each rebased to its own 100, now COLORED + carrying raw kg.
      // colorFor (no `i % len`): con mas ejercicios que colores, el excedente sale gris en vez de
      // repetir un tono y hacer pasar dos series por una.
      view.exOrdered.forEach((ex, i) => add({
        key: `ex-${ex.name}`, name: ex.name,
        pts: rebased(ex), raw: ex.points.map((p) => p.value),      // e1RM kg, 1:1 with rebased(ex)
        // rebased() colapsa el punto a date/t/value, asi que las banderas se leen de la serie cruda.
        meta: ex.points.map((p) => ({ lowConf: p.lowConf, rirImputed: p.rirImputed })),
        color: colorFor(i), width: 1.2, opacity: 0.82,
      }))
      add({
        key: 'grp', name: view.selGroup.label, pts: view.selGroup.points,
        meta: view.selGroup.points.map(metaOfIndex),
        color: 'var(--volt)', width: 2.4, opacity: 1, bold: true,
      })
    } else {
      // FOCO + CONTEXTO: 10 lineas de colores compitiendo con la general la volvian ilegible y
      // ninguna era realmente comparable con las otras (cada indice tiene su propia base 100). Los
      // grupos entran como contexto gris y el usuario promueve UNO a su color tocando su chip.
      // Aqui nacen en REPOSO (gris); hue/groupKey dejan que el render las promueva sin invalidar
      // nada de lo memoizado.
      model.groups.forEach((g, i) => add({
        key: g.group, name: g.label, pts: g.points, meta: g.points.map(metaOfIndex),
        color: DIM_COLOR, width: 1, opacity: DIM_OPACITY, dim: true, hue: colorFor(i), groupKey: g.group,
      }))
      add({
        key: 'overview', name: 'general', pts: model.overview, meta: model.overview.map(metaOfIndex),
        color: 'var(--volt)', width: 2.2, opacity: 1, bold: true,
      })
    }

    const allPts = lines.flatMap((l) => l.pts)
    if (allPts.length === 0) return null
    const tMin = Math.min(...allPts.map((p) => p.t)), tMax = Math.max(...allPts.map((p) => p.t))
    const firstDate = allPts.reduce((a, p) => (p.t < a.t ? p : a), allPts[0]).date
    const lastDate = allPts.reduce((a, p) => (p.t > a.t ? p : a), allPts[0]).date
    let vLo = Math.min(...allPts.map((p) => p.value), 100), vHi = Math.max(...allPts.map((p) => p.value), 100)
    const pad = Math.max(2, (vHi - vLo) * 0.12); vLo -= pad; vHi += pad
    const xAt = (t: number) => (tMax === tMin ? (PLOT.x0 + PLOT.x1) / 2 : PLOT.x0 + ((t - tMin) / (tMax - tMin)) * (PLOT.x1 - PLOT.x0))
    const yAt = (v: number) => (vHi === vLo ? (PLOT.y0 + PLOT.y1) / 2 : PLOT.y1 - ((v - vLo) / (vHi - vLo)) * (PLOT.y1 - PLOT.y0))

    // Glifos de cambio de canasta, agrupados para que dos eventos en dias contiguos no se pisen.
    const clusters = clusterEvents(view.events, (d) => xAt(parseDay(d).getTime()), GLYPH_MIN_GAP)

    // flat hit list in draw order (bold pushed LAST → wins exact-overlap ties = "tap what's on top").
    // Los glifos van PRIMEROS: nearest() usa '<=' y conserva el ultimo mejor, asi que ante empate
    // exacto gana el punto de datos, que es el que el usuario esta mirando.
    const hits: ActivePt[] = [
      ...clusters.map((c) => ({
        cx: c.x, cy: GLYPH_Y,
        // El encabezado se elige por lo que trae el glifo: prometer "incluye" cuando algo SALE
        // seria falso (SF5/SF13/SF16).
        rows: [dateLabel(c.events[0].date), leadFor(c.events), ...eventRows(c.events)],
        notes: 0,
      })),
      ...lines.flatMap((l) =>
        l.pts.map((p, i) => {
          const note = noteFor(l.meta[i], i)                              // 3a fila solo si hay algo que confesar
          const rows = [
            dateLabel(p.date),                                            // 'lun 27 jul'
            l.raw
              ? `${clip(l.name)} · máx. ≈${Math.round(l.raw[i])} kg`      // exercise → estimated 1RM (honest ~)
              : `${clip(l.name)} · índice ${Math.round(p.value)}`,        // aggregate → 100-baseline index
          ]
          if (note) rows.push(note)
          return { cx: xAt(p.t), cy: yAt(p.value), rows, notes: note ? 1 : 0 }
        }),
      ),
    ]

    return { lines, firstDate, lastDate, y100: yAt(100), xAt, yAt, clusters, hits }
  }, [view, model])

  const chip = (active: boolean): React.CSSProperties => ({
    padding: '5px 10px', borderRadius: 'var(--r-chip)', fontSize: 12, fontWeight: 600,
    background: active ? 'var(--volt)' : 'var(--surface-2)', color: active ? 'var(--bg)' : 'var(--ink-2)',
    border: active ? '1px solid var(--volt)' : '1px solid var(--line-08)',
  })

  if (groups.length === 0 || !geom) {
    return (
      <div className="card">
        <div className="kick">fuerza</div>
        <p className="tip" style={{ marginTop: 8 }}>
          Aún sin datos de fuerza. A medida que registres el peso y las reps de tus series, aquí verás la
          tendencia de fuerza por grupo muscular (índice que combina todos tus ejercicios del grupo).
        </p>
      </div>
    )
  }

  const { firstDate, lastDate, y100, xAt, yAt, clusters, hits } = geom
  // El resaltado es SOLO estilo: promueve una linea de grupo de su gris de contexto a su color, sin
  // tocar ni un numero de la geometria memoizada (por eso `focus` no entra en las deps de geom).
  const lines = geom.lines.map((l) =>
    l.groupKey && focus === l.groupKey
      ? { ...l, color: l.hue!, width: 1.6, opacity: 0.95, dim: false }
      : l)

  const HIT_R = 14 // hit radius in viewBox units (~13 CSS px @390w) - fat-finger tolerant
  function nearest(ux: number, uy: number): ActivePt | null {
    let best: ActivePt | null = null, bestD = HIT_R * HIT_R
    for (const h of hits) {
      const dx = h.cx - ux, dy = h.cy - uy, d = dx * dx + dy * dy
      if (d <= bestD) { bestD = d; best = h }                        // '<=' → later-drawn (bold) wins ties
    }
    return best
  }

  // PointerEvent → viewBox user space. getScreenCTM bakes in width:100% scale + any letterbox; tsc-clean.
  function pick(e: React.PointerEvent<SVGSVGElement>) {
    const ctm = svgRef.current?.getScreenCTM(); if (!ctm) return     // detached / display:none
    const u = new DOMPoint(e.clientX, e.clientY).matrixTransform(ctm.inverse())
    const h = nearest(u.x, u.y)
    // identity-dedupe throttles hover; on TOUCH, tapping the same point again dismisses (discoverable)
    setActive((prev) => {
      if (prev && h && prev.cx === h.cx && prev.cy === h.cy) return e.pointerType === 'mouse' ? prev : null
      return h
    })
  }

  // ---- badge honesto ----
  // Tres regimenes en vez de "palabra + cifra siempre": la cifra solo aparece cuando la señal es
  // lo bastante grande como para que el ruido de medicion (~5-8% por sesion) no la explique.
  //  - dentro del umbral        -> "estable" (ya lo decide trendOf);
  //  - ZONA GRIS (1 a 2 umbrales) -> solo la palabra: hay direccion, pero poner "+9%" seria
  //    prometer una precision que el dato no tiene;
  //  - señal fuerte (> 2 umbrales) -> palabra, cifra aproximada y sobre cuantas sesiones.
  // El asterisco avisa de que la composicion del indice cambio dentro de la ventana (entro, salio o
  // volvio algo), y su significado se explica en TEXTO VISIBLE bajo el grafico, no solo en el title
  // (que ni el teclado ni el tacto alcanzan). Va en TODOS los regimenes que leen tendencia, tambien
  // en "estable" (SF12): el pie ya promete que el asterisco avisa, y quedarse sin asterisco mientras
  // la frase lo anuncia deja al usuario buscando algo que no esta. En "datos insuficientes" no
  // aplica: ahi trendOf ni siquiera lee la ventana, asi que nunca reporta cambio de canasta.
  const strong = Math.abs(tr.deltaPct) > 2 * TREND_GATE * 100
  const star = tr.basketChanged ? '*' : ''
  const header = tr.trend === 'insuficiente' ? 'datos insuficientes'
    : tr.trend === 'estable' ? `estable${star}`
      : strong
        // "sesiones" completo, no "ses." (SF19: espanol llano en toda la superficie). El caso mas
        // largo, "mejorando ~+15% · 12 sesiones*", mide ~182px a 12.5px/600; con el badge en una
        // sola linea (whiteSpace nowrap) el bloque de la izquierda se parte antes que el badge y
        // los dos caben en los 320px utiles de la tarjeta a 390px de pantalla.
        ? `${tr.trend} ~${tr.deltaPct > 0 ? '+' : '-'}${Math.round(Math.abs(tr.deltaPct))}% · ${tr.nPoints} sesiones${star}`
        : `${tr.trend}${star}`
  const headerColor = tr.trend === 'mejorando' ? 'var(--volt)' : tr.trend === 'bajando' ? 'var(--warn)' : 'var(--ink-3)'
  const windowTxt = tr.windowDays ? `últimas ${Math.round(tr.windowDays / 7)} semanas` : 'toda tu historia'
  const headerTitle = tr.trend === 'insuficiente'
    ? 'Con 3 sesiones o más se puede leer una tendencia.'
    : `${tr.deltaPct > 0 ? '+' : ''}${tr.deltaPct.toFixed(1)}% sobre ${tr.nPoints} sesiones (${windowTxt}).`

  // Pie de marcas: nombra SOLO lo que esta en pantalla (prometer una leyenda de algo invisible es
  // ruido). Los marcadores solo se dibujan en las lineas que no son de contexto.
  const anyLowConf = lines.some((l) => !l.dim && l.meta.some((m, i) => markerKind(m, i) === 'hollow'))
  const anyProvisional = lines.some((l) => !l.dim && l.meta.some((m) => m.provisional))
  const anyCarried = lines.some((l) => l.meta.some((m, i) => i > 0 && m.obs === 0))

  return (
    <div className="card">
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
        <div className="kick">fuerza{selGroup ? ` · ${selGroup.label.toLowerCase()}` : ' · índice'}</div>
        <span title={headerTitle} style={{ fontSize: 12.5, fontWeight: 600, color: headerColor, whiteSpace: 'nowrap' }}>{header}</span>
      </div>

      {/* role=img + aria-label carries the series identity for assistive tech (the visual endpoint
          name-label was removed - it clipped past the viewBox; the header/chip/legend name it visually).
          El aria-label suma el resaltado activo y cuantos cambios de composicion hay en el periodo:
          los glifos son visuales, la misma historia tiene que llegar por texto. */}
      <svg ref={svgRef} viewBox={`0 0 ${PLOT.w} ${PLOT.h}`} width="100%" preserveAspectRatio="xMidYMid meet" role="img"
        aria-label={`Índice de fuerza${selGroup ? ` · ${selGroup.label.toLowerCase()}` : ' · todas'}: ${header}. ${headerTitle}`
          + (focus ? ` Resaltado: ${groups.find((g) => g.group === focus)?.label ?? ''}.` : '')
          + (events.length ? ` ${events.length} ${events.length === 1 ? 'cambio' : 'cambios'} en lo que incluye el índice.` : '')}
        style={{ display: 'block', marginTop: 10, touchAction: 'pan-y' }}
        onPointerDown={(e) => { downRef.current = { x: e.clientX, y: e.clientY } }}
        onPointerUp={(e) => {
          const d = downRef.current; downRef.current = null
          if (d && Math.hypot(e.clientX - d.x, e.clientY - d.y) < 10) pick(e) // commit a TAP only if the finger barely moved (not a scroll)
        }}
        onPointerCancel={() => { downRef.current = null }}
        onPointerMove={(e) => { if (e.pointerType === 'mouse') pick(e) }}
        onPointerLeave={(e) => { if (e.pointerType === 'mouse') setActive(null) }}>
        {/* 100 baseline reference - the honest zero-equivalent (everything is relative to it) */}
        <line x1={PLOT.x0} y1={y100} x2={PLOT.x1} y2={y100} stroke="var(--line-12)" strokeWidth={1} />
        <text x={PLOT.x1 + 4} y={y100 + 3} fontSize={9} fill="var(--ink-4)" fontFamily="var(--font-display)">100</text>

        {/* Cambios de canasta: regla vertical discontinua + glifo en el canal libre de arriba. Se
            dibujan ANTES que las series para que queden por detras del trazo. */}
        {clusters.map((c, i) => (
          <g key={`ev-${i}`}>
            <line x1={c.x} y1={GLYPH_Y + 5} x2={c.x} y2={PLOT.y1} stroke={DIM_COLOR} strokeOpacity={0.45}
              strokeWidth={1} strokeDasharray={CARRIED_DASH} />
            <circle cx={c.x} cy={GLYPH_Y} r={4.6} fill="var(--surface-2)" stroke={DIM_COLOR} strokeWidth={1} />
            <text x={c.x} y={GLYPH_Y + 2.6} textAnchor="middle" fontSize={7} fill="var(--ink-3)"
              fontFamily="var(--font-body)">{c.events.length > 1 ? c.events.length : '+'}</text>
          </g>
        ))}

        {lines.map((l) => {
          // Los tramos vienen partidos desde el memo de geometria (segmentsOf separa los MEDIDOS de
          // los arrastrados, que van punteados: dibujar todo continuo afirmaba una medicion que no
          // existio). Aqui solo se pintan.
          return (
            <g key={l.key}>
              {l.segs.map((s, si) => (
                <polyline key={si} fill="none" stroke={l.color} strokeOpacity={l.opacity} strokeWidth={l.width}
                  strokeLinejoin="round" strokeLinecap="round" strokeDasharray={s.dashed ? CARRIED_DASH : undefined}
                  points={s.idx.map((i) => `${xAt(l.pts[i].t)},${yAt(l.pts[i].value)}`).join(' ')} />
              ))}
              {/* Las lineas de contexto (grises) van SIN marcadores: con 9 grupos serian ~117
                  circulos compitiendo con la serie que el usuario esta leyendo. */}
              {!l.dim && l.pts.map((p, i) => {
                const kind = markerKind(l.meta[i], i)
                if (kind === 'none') return null            // paso arrastrado: el tramo punteado ya lo dice
                const hollow = kind === 'hollow'
                // Hueco = medicion menos fiable. Radio 2.8 y trazo 1.4 (no 2.2/1.2): a 390px de
                // ancho un aro mas fino no se distingue del relleno, y esta es justo la marca que
                // no puede pasar desapercibida. La opacidad se fuerza a 1 por lo mismo.
                const r = hollow ? 2.8 : l.bold ? 3 : 2.2
                const cx = xAt(p.t), cy = yAt(p.value)
                return (
                  <g key={i}>
                    {l.meta[i]?.provisional && (
                      <circle cx={cx} cy={cy} r={r + 2.4} fill="none" stroke={l.color} strokeOpacity={l.opacity} strokeWidth={0.8} />
                    )}
                    <circle cx={cx} cy={cy} r={r}
                      fill={hollow ? 'var(--surface-1)' : l.color} fillOpacity={hollow ? 1 : l.opacity}
                      stroke={l.color} strokeOpacity={hollow ? 1 : l.opacity} strokeWidth={hollow ? 1.4 : 1.2}
                      strokeDasharray={hollow ? LOWCONF_DASH : undefined} />
                  </g>
                )
              })}
            </g>
          )
        })}

        <text x={PLOT.x0} y={PLOT.h - 4} fontSize={9} fill="var(--ink-4)" fontFamily="var(--font-body)">{dateLabel(firstDate)}</text>
        <text x={PLOT.x1} y={PLOT.h - 4} fontSize={9} fill="var(--ink-4)" textAnchor="end" fontFamily="var(--font-body)">{dateLabel(lastDate)}</text>

        {/* A11y: sighted-only visual affordance. role=img + aria-label (trend header), the colored
            legend, and the chip carry the accessible story; per-point kg/índice detail is intentionally
            NOT keyboarded/exposed to AT (visual-only trend proxy). Tooltip is aria-hidden +
            pointerEvents=none so it never enters the a11y tree or eats a dismiss tap. */}
        {active && (() => {
          const FS = 9, CW = 5.6, PADX = 7, PADY = 5, LH = 11, GAP = 8   // CW = est. glyph advance @FS (NO DOM measure)
          // Tope duro de filas: un cluster de cambios podria traer mas y la caja se saldria por
          // abajo del viewBox (a 390px eso es la pantalla entera). eventRows ya acota a 4, MAX_ROWS
          // es el cinturon de seguridad.
          const rows = active.rows.slice(0, MAX_ROWS)
          const bw = Math.ceil(PADX * 2 + Math.max(...rows.map((s) => s.length)) * CW)
          const bh = PADY * 2 + rows.length * LH
          const bx = Math.max(2, Math.min(active.cx - bw / 2, PLOT.w - 2 - bw))  // center + clamp X inside 340
          let by = active.cy - GAP - bh                                          // prefer ABOVE the dot
          if (by < 2) by = Math.min(active.cy + GAP, PLOT.h - 2 - bh)            // flip BELOW, clamp bottom
          const cx = bx + bw / 2
          const muted = (i: number) => i === 0 || i >= rows.length - active.notes // fecha + notas
          return (
            <g aria-hidden="true" pointerEvents="none">
              <circle cx={active.cx} cy={active.cy} r={4.5} fill="none" stroke="var(--volt)" strokeWidth={1.3} />
              <rect x={bx} y={by} width={bw} height={bh} rx={6} ry={6} fill="var(--surface-2)" stroke="var(--line-12)" strokeWidth={1} />
              {rows.map((s, i) => (
                <text key={i} x={cx} y={by + PADY + FS - 1 + i * LH} textAnchor="middle" fontSize={FS}
                  fill={muted(i) ? 'var(--ink-3)' : 'var(--ink-1)'}
                  fontFamily={muted(i) ? 'var(--font-body)' : 'var(--font-display)'}>{s}</text>
              ))}
            </g>
          )
        })()}
      </svg>

      {/* legend below the plot - avoids the inline endpoint-label collision when lines cluster near 100 */}
      {selGroup ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 12px', marginTop: 6 }}>
          {exOrdered.map((ex, i) => (
            <span key={ex.name} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10.5, color: 'var(--ink-3)' }}>
              <span style={{ width: 9, height: 2, borderRadius: 1, background: colorFor(i) }} />{ex.name}
            </span>
          ))}
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10.5, color: 'var(--volt)', fontWeight: 600 }}>
            <span style={{ width: 11, height: 2.5, borderRadius: 1, background: 'var(--volt)' }} />{selGroup.label.toLowerCase()}
          </span>
        </div>
      ) : (
        // La leyenda deja de ser decorativa: cada grupo es un boton de dos estados que lo promueve
        // del gris de contexto a su color. Escape limpia el resaltado para quien navega por teclado.
        <div role="group" aria-label="Resaltar un grupo"
          onKeyDown={(e) => { if (e.key === 'Escape') setFocus(null) }}
          style={{ display: 'flex', flexWrap: 'wrap', gap: '2px 8px', marginTop: 4 }}>
          {groups.map((g, i) => {
            const on = focus === g.group
            return (
              <button key={g.group} type="button" className="legendBtn" aria-pressed={on}
                onClick={() => setFocus(on ? null : g.group)}>
                {/* el swatch usa EL MISMO color que la linea en los dos estados (gris de contexto o
                    su hue); apenas un punto mas opaco porque 9x2 px al 0.30 no se verian */}
                <span style={{ width: 9, height: on ? 2.5 : 2, borderRadius: 1, background: on ? colorFor(i) : DIM_COLOR, opacity: on ? 1 : DIM_OPACITY + 0.25 }} />{g.label}
              </button>
            )
          })}
          <span className="legendBtn" style={{ color: 'var(--volt)', fontWeight: 600 }}>
            <span style={{ width: 11, height: 2.5, borderRadius: 1, background: 'var(--volt)' }} />general
          </span>
        </div>
      )}

      {/* Pie de marcas: solo describe lo que hay en pantalla ahora mismo. */}
      <p className="muted" style={{ fontSize: 10.5, marginTop: 4, lineHeight: 1.45 }}>
        {tr.trend === 'insuficiente'
          ? 'Menos de 3 sesiones: se muestran los puntos, aún sin trazar tendencia. '
          : `Tendencia sobre ${tr.nPoints} ${tr.nPoints === 1 ? 'sesión' : 'sesiones'} (${windowTxt}). `}
        {!selGroup && (focus
          ? `Resaltado: ${groups.find((g) => g.group === focus)?.label ?? ''}. Vuelve a tocarlo para quitarlo. `
          : 'Elige un grupo de la lista para resaltarlo. ')}
        {anyLowConf && 'Los puntos huecos son mediciones menos fiables. '}
        {anyProvisional && 'El anillo marca un dato fuera de rango que se recortó. '}
        {anyCarried && 'El tramo punteado es un valor arrastrado, sin medición nueva. '}
        {clusters.length > 0 && 'Las marcas de arriba señalan desde cuándo entró o salió algo del índice. '}
        {tr.basketChanged && 'El asterisco avisa de que entró o salió algo dentro del periodo medido.'}
      </p>

      <div role="group" aria-label="Ver un grupo" style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
        <button type="button" style={chip(sel === 'all')} onClick={() => setSel('all')}>Todas</button>
        {groups.map((g) => (
          <button key={g.group} type="button" style={chip(sel === g.group)} onClick={() => setSel(g.group)}>{g.label}</button>
        ))}
      </div>
    </div>
  )
}
